import { AnimatePresence, motion } from 'motion/react';
import { X, Clock, ChevronDown, ChevronRight, GitCommit, Upload, RefreshCw, Copy } from 'lucide-react';
import { Button, ScrollArea } from '@higgins/ui';
import { cn } from '@higgins/utils';
import { useReportHistory } from '@/lib/hooks';
import type { ReportAuditEntry, ChangeType } from '@/lib/types';
import { useState } from 'react';

interface VersionPanelProps {
  reportId: string;
  open: boolean;
  onClose: () => void;
}

const CHANGE_TYPE_META: Record<ChangeType, { label: string; icon: React.ReactNode; color: string }> = {
  Edit:          { label: 'Edited',       icon: <GitCommit className="w-3 h-3" />,  color: 'bg-slate-100 text-slate-600' },
  StatusChange:  { label: 'Status',       icon: <Clock className="w-3 h-3" />,      color: 'bg-blue-100 text-blue-700' },
  Publish:       { label: 'Published',    icon: <Upload className="w-3 h-3" />,     color: 'bg-green-100 text-green-700' },
  Clone:         { label: 'Cloned',       icon: <Copy className="w-3 h-3" />,       color: 'bg-purple-100 text-purple-700' },
  VisualRefresh: { label: 'Visuals',      icon: <RefreshCw className="w-3 h-3" />,  color: 'bg-orange-100 text-orange-700' },
};

const SECTION_LABELS: Record<string, string> = {
  exec_summary: 'Exec Summary',
  safety: 'Safety',
  customer: 'Customer',
  sales: 'Sales',
  operations: 'Operations',
  financial: 'Financial',
};

const FIELD_LABELS: Record<string, string> = {
  content: 'Commentary',
  content2: 'Secondary',
  content3: 'Tertiary',
  status: 'Status',
  visualStatus: 'Visual Status',
};

function formatTime(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
}

function truncate(s: string, n = 80) {
  const plain = stripHtml(s);
  return plain.length > n ? plain.slice(0, n) + '…' : plain;
}

function DiffView({ before, after }: { before: string; after: string }) {
  const plainBefore = stripHtml(before);
  const plainAfter  = stripHtml(after);
  return (
    <div className="mt-2 space-y-1.5 text-xs">
      {plainBefore && (
        <div className="rounded bg-red-50 border border-red-100 px-2 py-1.5 text-red-800 font-mono whitespace-pre-wrap leading-relaxed">
          <span className="select-none text-red-400 mr-1">−</span>{plainBefore}
        </div>
      )}
      {plainAfter && (
        <div className="rounded bg-green-50 border border-green-100 px-2 py-1.5 text-green-800 font-mono whitespace-pre-wrap leading-relaxed">
          <span className="select-none text-green-400 mr-1">+</span>{plainAfter}
        </div>
      )}
    </div>
  );
}

function AuditEntryRow({ entry }: { entry: ReportAuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const meta = CHANGE_TYPE_META[entry.changeType];
  const hasContent = !!(entry.valueBefore || entry.valueAfter);

  return (
    <div className="border-b border-slate-100 last:border-0 py-3 px-4">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0">
          <div className={cn('inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium', meta.color)}>
            {meta.icon}
            {meta.label}
          </div>
        </div>
        <div className="flex-1 min-w-0 space-y-0.5">
          <p className="text-xs font-medium text-slate-700 leading-tight">
            {entry.changedByName}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {entry.fieldName in FIELD_LABELS ? FIELD_LABELS[entry.fieldName] : entry.fieldName}
            {entry.sectionId && (
              <span className="text-slate-400"> · {SECTION_LABELS[entry.fieldName] ?? entry.fieldName}</span>
            )}
          </p>
          {!expanded && hasContent && entry.valueAfter && (
            <p className="text-[11px] text-slate-500 italic truncate">
              {truncate(entry.valueAfter)}
            </p>
          )}
        </div>
        <div className="shrink-0 text-right space-y-1">
          <p className="text-[10px] text-muted-foreground whitespace-nowrap">{formatTime(entry.changedOn)}</p>
          {hasContent && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-[10px] text-primary hover:underline flex items-center gap-0.5 ml-auto"
            >
              {expanded
                ? <><ChevronDown className="w-2.5 h-2.5" />less</>
                : <><ChevronRight className="w-2.5 h-2.5" />diff</>
              }
            </button>
          )}
        </div>
      </div>

      {expanded && hasContent && (
        <DiffView before={entry.valueBefore} after={entry.valueAfter} />
      )}
    </div>
  );
}

export function VersionPanel({ reportId, open, onClose }: VersionPanelProps) {
  const { data: history = [], isLoading } = useReportHistory(reportId, open);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — click to close */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-20 bg-black/10"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.aside
            key="panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 400, damping: 40 }}
            className="fixed right-0 top-0 h-full z-30 w-80 bg-white shadow-2xl border-l border-slate-200/60 flex flex-col"
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200/60 shrink-0">
              <div>
                <p className="text-sm font-semibold text-slate-900">Version History</p>
                {history.length > 0 && (
                  <p className="text-[11px] text-muted-foreground">{history.length} changes</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
                <X className="w-4 h-4" />
              </Button>
            </header>

            <ScrollArea className="flex-1">
              {isLoading && (
                <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                  Loading history…
                </div>
              )}

              {!isLoading && history.length === 0 && (
                <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                  No changes recorded yet.
                </div>
              )}

              {history.map(entry => (
                <AuditEntryRow key={entry.id} entry={entry} />
              ))}
            </ScrollArea>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
